"use client";

import { ComponentPropsWithoutRef, FC, ReactNode } from "react";
import { useLocale, useMessages } from "../context/i18n";
import { common as enCommon } from "../dictionaries/en/common";
import avatar0 from "../public/images/trusted_by/0.jpg";
import avatar1 from "../public/images/trusted_by/1.jpg";
import avatar2 from "../public/images/trusted_by/2.jpg";
import avatar3 from "../public/images/trusted_by/3.jpg";
import avatar5 from "../public/images/trusted_by/5.jpg";
import { formatNumber } from "../utils/format-number";
import Avatar from "./Avatar";
import Typography from "./Typography";

type TrustedByProps = {
  variant?: "dark" | "light";
  usersCount?: number;
  // Override the default badge label. When provided, replaces the
  // entire "Trusted by … happy users" string. v2 marketing pages pass
  // "8.5M clips made by 3.5M creators" per the SEO master spec.
  label?: ReactNode;
} & ComponentPropsWithoutRef<"div">;

const TrustedBy: FC<TrustedByProps> = ({
  variant,
  usersCount,
  label,
  ...rest
}) => {
  const lang = useLocale();
  const m = useMessages("common", enCommon);
  const formattedUsersCount =
    typeof usersCount === "number" && Number.isFinite(usersCount)
      ? formatNumber(usersCount, lang)
      : undefined;

  // Source of truth confirmed by product (May 2026): the homepage and
  // every v2 SEO tool page should render the same social-proof string.
  // Callers can still pass an explicit `label` or `usersCount` to
  // override (e.g. localized variants), but the default is the new
  // unified copy — the legacy "2.3M+ happy users" wording is retired.
  const defaultLabel = (
    <>
      <strong className="text-slate-800 dark:text-foreground !font-medium">
        {m.statClips}
      </strong>
      {m.statMadeBy}
      <strong className="text-slate-800 dark:text-foreground !font-medium">
        {m.statCreators}
      </strong>
    </>
  );

  const resolvedLabel =
    label ??
    (formattedUsersCount ? (
      <>
        {m.trustedBy}
        <strong className="text-slate-800 dark:text-foreground !font-medium">
          {m.happyUsers.replace("{count}", formattedUsersCount)}
        </strong>
      </>
    ) : (
      defaultLabel
    ));

  return (
    <div
      {...rest}
      className={`${rest.className} dark:text-foreground-secondary flex gap-2 items-center justify-center bg-slate-100 dark:bg-surface-secondary rounded-full px-2 py-1 pr-4`}
    >
      {/* Descriptive avatar alts so screen readers (and crawlers
          looking for E-E-A-T signals) get something better than five
          "Avatar" repeats. Real first names are not yet sourced from
          the production user list — `[VERIFY: real first names from
          production user list]` — so we fall back to a generic but
          accurate "Klap user" label for now. */}
      <div className="flex">
        <Avatar
          src={avatar0}
          alt={m.userAlt}
          className={`w-4 h-4 !bg-black border-2 dark:border-surface-secondary border-slate-100`}
        />
        <Avatar
          src={avatar1}
          alt={m.userAlt}
          className={`-ml-1.5 w-4 h-4 !bg-black border-2 dark:border-surface-secondary border-slate-100`}
        />
        <Avatar
          src={avatar2}
          alt={m.userAlt}
          className={`-ml-1.5 w-4 h-4 !bg-black border-2 dark:border-surface-secondary border-slate-100`}
        />
        <Avatar
          src={avatar3}
          alt={m.userAlt}
          className={`-ml-1.5 w-4 h-4 !bg-black border-2 dark:border-surface-secondary border-slate-100`}
        />
        <Avatar
          src={avatar5}
          alt={m.userAlt}
          className={`-ml-1.5 w-4 h-4 !bg-black border-2 dark:border-surface-secondary border-slate-100`}
        />
      </div>
      <Typography variant="caption" className="text-left text-slate-500">
        {resolvedLabel}
      </Typography>
    </div>
  );
};

export default TrustedBy;
